import React, { useEffect, useState } from 'react'
import { Col, Row, FormGroup, Input, Alert } from 'reactstrap'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import Switch from 'react-switch'
import { useFormUtils } from 'views/shared/useFormUtils'
import PropTypes from 'prop-types'
import { isNotNumericDecimal } from 'context/Utilities'

const PlantYogurtNutritionReview = ({ item, isDisabled }) => {
  const [packaging, setPackaging] = useState({
    VitaminD: item.VitaminD,
    Sugar: item.Sugar,
    Calcium: item.Calcium,
    Protein: item.Protein,
    SweetenerCertification: item.SweetenerCertification,
    SugarCertification: item.SugarCertification,
    CalciumCertification: item.CalciumCertification,
    ProteinCertification: item.ProteinCertification,
  })

  const methods = useFormContext()
  const { setSwitchReviewer, onChangeReviewer, asterisk } = useFormUtils()

  useEffect(() => {
    if (item !== null) {
      const {
        VitaminD,
        Sugar,
        Calcium,
        Protein,
        SweetenerCertification,
        SugarCertification,
        CalciumCertification,
        ProteinCertification,
      } = item
      setPackaging({
        VitaminD,
        Sugar,
        Calcium,
        Protein,
        SweetenerCertification,
        SugarCertification,
        CalciumCertification,
        ProteinCertification,
      })
    }
  }, [item])

  const renderField = (
    name,
    label,
    maxLength,
    placeholder,
    min,
    pattern,
    message
  ) => (
    <FormGroup>
      <p>
        <strong>
          {label} {asterisk}
        </strong>
      </p>
      <Controller
        as={Input}
        type="number"
        step="0.01"
        name={name}
        placeholder={placeholder}
        defaultValue={packaging[name]}
        control={methods.control}
        rules={{
          required: `⚠ Please enter ${label}`,
          maxLength: {
            value: maxLength,
            message: `⚠ ${label} exceeds ${maxLength} characters`,
          },
          min: min !== null ? { value: min, message: message } : undefined,
          pattern: {
            value: pattern,
            message: '⚠ Maximum two decimal places allowed.',
          },
        }}
        onKeyDown={e => isNotNumericDecimal(e.key) && e.preventDefault()}
        onChange={([event]) => {
          onChangeReviewer(item, event)
          return event
        }}
        disabled={isDisabled}
      />
      <ErrorMessage
        errors={methods.errors}
        name={name}
        as={<Alert color="danger" className="mt-3" />}
      />
    </FormGroup>
  )

  const renderSwitch = (name, label, errorMessage) => (
    <FormGroup>
      <p>
        <strong>
          {label} {asterisk}
        </strong>
      </p>
      <Controller
        as={Switch}
        name={name}
        id={name}
        defaultValue={packaging[name]}
        onChange={([checked, event, id]) => {
          setSwitchReviewer(item, checked, event, id)
          return checked
        }}
        control={methods.control}
        rules={{ validate: value => value === true }}
        disabled={isDisabled}
      />
      <ErrorMessage
        errors={methods.errors}
        name={name}
        message={errorMessage}
        as={<Alert color="danger" className="mt-3" />}
      />
    </FormGroup>
  )

  return (
    <>
      <Row>
        <Col xs={6}>
          {renderField(
            'VitaminD',
            'Vitamin D (mcg) per 8 ounces',
            4,
            '',
            null,
            /^\d+(\.\d{1,2})?$/,
            ''
          )}
        </Col>
        <Col xs={6}>
          {renderSwitch(
            'SweetenerCertification',
            'I certify that this product does not contain artificial sweeteners, reduced-calorie sweeteners, or no-calorie sweeteners.',
            '⚠ You must certify that this product does not contain artificial sweeteners, reduced-calorie sweeteners, or no-calorie sweeteners.'
          )}
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <FormGroup>
            <p>
              <strong>Added Sugar (g) per 8 ounces {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="Sugar"
              defaultValue={packaging.Sugar}
              control={methods.control}
              onKeyDown={e => isNotNumericDecimal(e.key) && e.preventDefault()}
              placeholder={'Max 16g added sugar per 8 ounces'}
              rules={{
                required: '⚠ Please enter Added Sugar (g) per 8 ounces.',
                maxLength: {
                  value: 4,
                  message:
                    '⚠ Added Sugar (g) per 8 ounces exceeds 4 characters.',
                },
                max: {
                  value: 16,
                  message:
                    '⚠ Added Sugar (g) per 8 ounces exceeds the 16g maximum allowed.',
                },
                pattern: {
                  value: /^\d+(\.\d{1,2})?$/,
                  message: '⚠ Maximum two decimal places allowed.',
                },
              }}
              onChange={([event]) => {
                onChangeReviewer(item, event)
                return event
              }}
              disabled={isDisabled}
            />
            <ErrorMessage
              errors={methods.errors}
              name="Sugar"
              as={<Alert color="danger" className="mt-3" />}
            />
          </FormGroup>
        </Col>
        <Col xs={6}>
          {renderSwitch(
            'SugarCertification',
            'I certify that this product contains no more than the maximum 16g total added sugar allowed per 8 ounces.',
            '⚠ You must certify that this product contains no more than the maximum 16g total added sugar allowed per 8 ounces.'
          )}
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          {renderField(
            'Calcium',
            'Calcium (mg) per 8 ounces',
            6,
            'Min 250mg calcium per 8 ounces',
            250,
            /^\d+(\.\d{1,2})?$/,
            '⚠ Calcium (mg) per 8 ounces is below the required 250mg minimum'
          )}
        </Col>
        <Col xs={6}>
          {renderSwitch(
            'CalciumCertification',
            'I certify that this product contains at least 250mg calcium per 8 ounces.',
            '⚠ You must certify that this product contains no less than the required minimum 250mg total calcium per 8 ounces.'
          )}
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          {renderField(
            'Protein',
            'Protein (g) per 8 ounces',
            4,
            'Min 6.5g protein per 8 ounces',
            6.5,
            /^\d+(\.\d{1,2})?$/,
            '⚠ Protein (g) per 8 ounces is below the required 6.5g minimum'
          )}
        </Col>
        <Col xs={6}>
          {renderSwitch(
            'ProteinCertification',
            'I certify that this product contains at least 6.5g protein per 8 ounces.',
            '⚠ You must certify that this product contains no less than the required minimum 6.5g total protein per 8 ounces.'
          )}
        </Col>
      </Row>
    </>
  )
}

PlantYogurtNutritionReview.propTypes = {
  item: PropTypes.object,
  isDisabled: PropTypes.bool,
}

export default PlantYogurtNutritionReview
